import { Alert, AlertTitle, Box, Button, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getSendingSessionTypeText } from 'src/shared/models/enums/InvoiceSendingTypeString.enum';
import { useTranslation } from 'react-i18next';
import { convertSendingTypeStringToInt } from 'src/modules/invoice/components/invoiceMissionSendingTypeConverter';
import { InvoiceMissionSendingSearchfilter } from 'src/shared/models/invoiceMission/iInvoiceMissionSendingSearchfilter';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { getAllInvoiceMissionSending } from 'src/shared/services/invoiceMissionSendingService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { InvoiceMissionSending } from '../../models/invoiceMissionSending';

export const InvoiceMissionApprovalAlert = () => {
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [, setLoading] = useState(false);
  const [invoiceMissionSending, setInvoiceMissionSending] = useState<InvoiceMissionSending[]>();
  const [hasSingleMission, setHasSingleMission] = useState<boolean>(false);
  const [searchFilter] = useState<InvoiceMissionSendingSearchfilter>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
    sortCol: 'invoiceMission_id',
    sortDir: 'desc',
    filter: '',
    isDenied: false,
    isApproved: false,
    isApprovalRequired: true,
  });

  const linkToMission = (sending: InvoiceMissionSending) => {
    if (hasSingleMission) {
      const convertSendingType = convertSendingTypeStringToInt(sending.sendingType);
      return navigate(`${RoutesEnum.InvoiceMissionDetailSendingType}/${sending.invoiceMission_id}/${convertSendingType}`);
    }
    return navigate(RoutesEnum.InvoiceMission);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllInvoiceMissionSending(searchFilter);
      if (isMounted()) {
        setInvoiceMissionSending(result.data.results);

        if (result.data.results && result.data.results.length === 1) {
          setHasSingleMission(true);
        }
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [isMounted, searchFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      {!invoiceMissionSending ? (
        <></>
      ) : (
        <>
          {invoiceMissionSending.length > 0 &&
            invoiceMissionSending.map((sending) => {
              return (
                <Alert
                  key={sending.id}
                  severity='warning'
                  sx={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'warning.main',
                    '& .MuiAlert-message': { width: '100% !important' },
                    mb: 2,
                  }}
                >
                  <AlertTitle>{t('dashboard:invoiceMissionApprovalAlert.notificationToApprove')}</AlertTitle>

                  {!hasSingleMission ? (
                    <Grid container alignItems='center' justifyContent='space-between'>
                      <Grid item>
                        <Typography mb={0.5}>{t('dashboard:invoiceMissionApprovalAlert.invoicesPendingApproval')}</Typography>
                      </Grid>
                      <Grid item>
                        <Button onClick={() => linkToMission(sending)} variant='contained' sx={{ textTransform: 'uppercase' }}>
                          {t('dashboard:invoiceMissionApprovalAlert.goToMissions')}
                        </Button>
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <Typography mb={0.5}>{t('dashboard:invoiceMissionApprovalAlert.invoicesPendingApproval')}</Typography>
                      <Grid container spacing={1} alignItems='center'>
                        {hasSingleMission && (
                          <Grid item xs={12} md={9} lg sx={{ mt: { xs: 1, md: 0 } }}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 1,
                              }}
                            >
                              <Link
                                to={`${RoutesEnum.InvoiceMissionDetailSendingType}/${sending.invoiceMission_id}/${convertSendingTypeStringToInt(
                                  sending.sendingType,
                                )}`}
                              >
                                {sending.invoiceMissionName}
                              </Link>

                              <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 2 } }}>
                                {sending.sendingType && (
                                  <CustomChip smallRadius label={getSendingSessionTypeText(sending.sendingType, t)} color='info' size='small' />
                                )}
                                {sending.deadline && (
                                  <CustomChip smallRadius label={DateHelper.getDateTime(sending.deadline)} color='error' size='small' />
                                )}
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        <Grid
                          item
                          xs={12}
                          sm='auto'
                          sx={{
                            mt: { xs: 1, sm: 1, md: 0 },
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                          }}
                        >
                          <Button onClick={() => linkToMission(sending)} variant='contained' sx={{ textTransform: 'uppercase' }}>
                            {t('dashboard:invoiceMissionApprovalAlert.reviewMission')}
                          </Button>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Alert>
              );
            })}
        </>
      )}
    </>
  );
};
